import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { toDateFormat } from '@restworld/utils-common';
import { IndexJobApplicationsParams, CheckInvoicesStatusResponse } from '@restworld/data-services';
import {
  JobApplication,
  JobApplicationStatusKey,
  MatchBreakdownItem,
  WorkerResearchInfo,
} from '@restworld/utility-types';

import { isCandidateStatusChangeDropdownOpenAtom } from './details/modals/candidate-modal/candidate-status-change-dropdown';

export type CandidateModalVariantType =
  | 'default'
  | 'worker_jp_employer_interested'
  | 'worker_jp_discarded'
  | 'state_change_confirmation'
  | 'worker_jp_discarded_confirmation'
  | 'hide_candidate_confirmation'
  | 'worker_jp_interview_scheduled_confirmation'
  | 'notes'
  | 'spontaneous_candidate'
  | 'spontaneous_candidate_notes'
  | 'worker_jp_recovery_confirmation'
  | 'references';

export type StatusKey = JobApplicationStatusKey | '';

export const employerJobListingSearchQueryAtom = atom('');
employerJobListingSearchQueryAtom.debugLabel = 'employerJobListingSearchQueryAtom';

export const employerJobListingSortValueAtom = atom('desc:relevance');
employerJobListingSortValueAtom.debugLabel = 'employerJobListingSortAtom';

export const employerJobListingLocationFilterAtom = atom('');
employerJobListingLocationFilterAtom.debugLabel = 'employerJobListingLocationFilterAtom';

export const employerJobListingJpStatusFilterAtom = atom<string[]>([]);
employerJobListingJpStatusFilterAtom.debugLabel = 'employerJobListingJpStatusFilterAtom';

export const employerJobListingRestaurantNameFilterAtom = atom('');
employerJobListingRestaurantNameFilterAtom.debugLabel = 'employerJobListingRestaurantNameFilterAtom';

export const invoiceStatusAtom = atom<CheckInvoicesStatusResponse>({ all_paid: null });
invoiceStatusAtom.debugLabel = 'invoiceStatusAtom';

export const candidateStateChangeAtom = atom<Partial<StatusKey>>('');

export const selectedJobApplicationAtom = atom<Partial<JobApplication>>({});
selectedJobApplicationAtom.debugLabel = 'selectedJobApplicationAtom';

export const showCandidateStateChangeModalAtom = atom<boolean>(false);
showCandidateStateChangeModalAtom.debugLabel = 'showCandidateStateChangeModalAtom';

export const candidateModalVariantAtom = atom<CandidateModalVariantType>('default');
candidateModalVariantAtom.debugLabel = 'candidateModalVariantAtom';

export const candidateStateChangeSelectionAtom = atom<StatusKey>('');

export const isCandidateHiredSuccessfullyAtom = atom<boolean>(false);

export const isCandidateModalSuccessScreenAtom = atom<boolean>(false);

export type MatchReviewModalData = {
  firstName: string;
  jobPositionId: string;
  jobOfferCorpusMarkdown: string;
  matchBreakdown: MatchBreakdownItem[];
};

// When non-null, the standalone match-review modal is open. Set after the
// employer accepts the consent prompt (see `matchReviewConsentDataAtom`).
export const matchReviewModalDataAtom = atom<MatchReviewModalData | null>(null as MatchReviewModalData | null);
matchReviewModalDataAtom.debugLabel = 'matchReviewModalDataAtom';

// When non-null, the small consent prompt is open — shown right after a
// successful discard, asking the employer if they want to see the
// educational match-review. Carries the same payload so we can hand it off
// to `matchReviewModalDataAtom` on accept without re-fetching anything.
export const matchReviewConsentDataAtom = atom<MatchReviewModalData | null>(null as MatchReviewModalData | null);
matchReviewConsentDataAtom.debugLabel = 'matchReviewConsentDataAtom';

export const useCandidateStateChangeModal = () => {
  const [showCandidateStateChangeModal, toggleCandidateStateChangeModal] = useAtom(showCandidateStateChangeModalAtom);
  const [, toggleCandidateStatusChangeDropdown] = useAtom(isCandidateStatusChangeDropdownOpenAtom);
  const [, setIsCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);
  const [, setIsCandidateHiredSuccessfully] = useAtom(isCandidateHiredSuccessfullyAtom);
  const [, setCandidateModalVariant] = useAtom(candidateModalVariantAtom);

  const closeCandidateModal = useCallback(() => {
    setIsCandidateModalSuccessScreen(false);
    setIsCandidateHiredSuccessfully(false);
    toggleCandidateStateChangeModal(false);
    toggleCandidateStatusChangeDropdown(false);
    setCandidateModalVariant('default');
  }, [
    setIsCandidateHiredSuccessfully,
    setIsCandidateModalSuccessScreen,
    toggleCandidateStateChangeModal,
    toggleCandidateStatusChangeDropdown,
    setCandidateModalVariant,
  ]);
  const openCandidateModal = useCallback(
    () => toggleCandidateStateChangeModal(true),
    [toggleCandidateStateChangeModal]
  );

  return {
    showCandidateStateChangeModal,
    openCandidateModal,
    closeCandidateModal,
  };
};

export const showDiscardedListModalAtom = atom(false);
// worker profile data
export const workerAtom = atom((get) => get(selectedJobApplicationAtom).worker);

export const workerPersonalDetailsAtom = selectAtom(workerAtom, (worker) => ({
  user_id: worker?.user_id,
  name: worker?.name,
  surname: worker?.surname,
  email: worker?.user?.email,
  phone_number: worker?.user?.phone_number,
  sex: worker?.sex_var?.label,
  birthdate: worker?.birthdate,
  address: worker?.address,
  communication_channel_preferences: worker?.communication_channel_preferences,
  other_communication_channel_preference: worker?.other_communication_channel_preference,
}));
export const workerTransportationDetailsAtom = selectAtom(workerAtom, (worker) => ({
  driving_licences: worker?.driving_licenses,
  transportation_options: worker?.transportation_options,
  displacement_availability_var: worker?.displacement_availability_var,
  displacement_preferences: worker?.displacement_preferences,
}));

export const workerPresentationTextAtom = selectAtom(workerAtom, (worker) => ({
  presentation_text: worker?.presentation,
  motivation_text: worker?.motivation_text,
}));

export const workerResearchAtom = selectAtom(workerAtom, (worker) => ({
  user_id: worker?.user_id,
  name: worker?.name,
  surname: worker?.surname,
  email: worker?.user?.email,
  research_info: worker?.research_info,
  research_status: worker?.research_status,
  research_status_last_updated: worker?.research_status_last_updated,
  picture: worker?.main_picture,
  cv: worker?.cv,
  public_cv_username: worker?.public_cv_username,
}));

export const workerMediaAtom = selectAtom(workerAtom, (worker) => ({
  picture: worker?.main_picture,
}));

export const researchStatusAtom = atom((get) => {
  const worker = get(workerAtom);
  return worker?.research_status;
});

export const workerPastExperiencesAtom = selectAtom(workerAtom, (worker) => worker?.past_experiences);

export const workerLanguagesAtom = selectAtom(workerAtom, (worker) => worker?.languages);

export const workerTrainingsAtom = selectAtom(workerAtom, (worker) => worker?.education_em);

export const workerCertificatesAtom = selectAtom(workerAtom, (worker) => worker?.certificate);

export const workerPreferencesAtom = selectAtom(workerAtom, (worker) => ({
  research_info: worker?.research_info,
  shifts: worker?.shifts,
  workerJobTypes: worker?.job_types,
}));

export const fullNameAtom = atom((get) => {
  const personalDetails = get(workerPersonalDetailsAtom);
  return personalDetails?.name || personalDetails?.surname
    ? `${personalDetails?.name || ''} ${personalDetails?.surname || ''}`
    : undefined;
});

export const birthDateAtom = atom((get) => {
  const personalDetails = get(workerPersonalDetailsAtom);
  return personalDetails?.birthdate ? toDateFormat(personalDetails.birthdate, 'dd/MM/yyyy') : undefined;
});

export const communicationPreferencesAtom = atom((get) => {
  const personalDetails = get(workerPersonalDetailsAtom);
  const communicationPreferences =
    personalDetails?.communication_channel_preferences
      ?.map((preference) => preference?.communication_channel_preference_var?.label)
      ?.filter((preference) => preference) || [];

  if (personalDetails?.other_communication_channel_preference) {
    communicationPreferences.push(personalDetails.other_communication_channel_preference);
  }

  const summaryCommunicationPreference =
    communicationPreferences.length > 0 ? communicationPreferences.join(', ') : undefined;

  return summaryCommunicationPreference;
});

export const drivingLicenceSummaryAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  const drivingLicences =
    transportationDetails.driving_licences
      ?.map((licence) => licence?.driving_license_var?.label)
      .filter((label) => label) || [];

  const drivingLicenceSummary = drivingLicences.length > 0 ? drivingLicences.join(', ') : undefined;
  return drivingLicenceSummary;
});

export const transportationOptionsAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  const transportationOptions =
    transportationDetails?.transportation_options
      ?.map((option) => option?.transportation_var?.label)
      ?.filter((label) => label) || [];
  return transportationOptions.length > 0 ? transportationOptions.join(', ') : undefined;
});

export const displacementAvailabilityAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  return transportationDetails?.displacement_availability_var?.label;
});

export const displacementPreferenceSummaryAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  const displacementPreferences =
    transportationDetails?.displacement_preferences
      ?.map((preference) => preference?.displacement_preference_var?.label)
      .filter((preference) => preference) || [];
  const displacementPreferenceSummary =
    displacementPreferences.length > 0 ? displacementPreferences.join(', ') : undefined;
  return displacementPreferenceSummary;
});

export const restaurantTypesAtom = atom((get) => {
  const workerResearchInfo = get(workerPreferencesAtom)?.research_info as unknown as WorkerResearchInfo;
  const restaurantTypes = (workerResearchInfo?.restaurant_types || [])
    .map((restaurantType) => restaurantType?.restaurant_type_var?.label)
    .filter((restaurantType) => !!restaurantType);
  return restaurantTypes.length > 0 ? restaurantTypes.join(', ') : undefined;
});

export const workerRolesAtom = atom((get) => {
  const workerResearchInfo = get(workerPreferencesAtom)?.research_info as unknown as WorkerResearchInfo;
  const roles = (workerResearchInfo?.occupations || [])
    .map((occupation) => occupation?.label_it?.label)
    .filter((occupation) => !!occupation);
  return roles.length > 0 ? roles.join(', ') : undefined;
});

export const workerShiftsAtom = atom((get) => {
  const workerShifts = get(workerPreferencesAtom)?.shifts;
  const shifts = (workerShifts || []).map((shift) => shift?.shift_var?.label).filter((shift) => !!shift);
  return shifts || undefined;
});

export const jobTypeAtom = atom((get) => {
  const jobTypes = get(workerPreferencesAtom)?.workerJobTypes;
  return jobTypes;
});

export const employerJobApplicationsInfiniteQueryParamsAtom = atom<IndexJobApplicationsParams>({});
